// readinessAlerts.js — confounder anomaly notifications for Readiness.
//
// Two patterns we treat as "before-you-train red flags":
//   1. Illness signature  — wristTempDeltaC > +0.3°C AND HRV ≥10% below avg
//   2. Dehydration mask   — overnightDeltaKg ≤ -1.5
//
// Both are computed deterministically in readiness.js (confounderAlert).
// This module handles the side-effect: firing an iOS local notification
// so the user sees the red flag in Notification Center even if they're
// not in the app at the moment. We dedupe per-day so the same alert
// doesn't fire twice on a single day, and we also schedule a daily
// 06:30 generic morning-check nudge that opens straight into Readiness.

import { LocalNotifications } from '@capacitor/local-notifications';

const DAY_KEY        = "sirat.hayah.readinessAlertFiredOn";
const MORNING_NOT_ID = 9100;       // generic 06:30 nudge
const ANOMALY_NOT_ID = 9101;       // anomaly push
const DEFAULT_HH     = 6;
const DEFAULT_MM     = 30;

function isNative() {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function ensurePermission() {
  if (!isNative()) return false;
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status?.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req?.display === 'granted';
  } catch { return false; }
}

// Fire the anomaly notification immediately (or rather: 2 seconds from
// now, the minimum iOS lets us schedule). Deduped per day + per kind so
// re-opening the app on the same day with the same condition doesn't
// re-fire. Returns true if it fired, false if it was skipped.
export async function fireConfounderAlertIfNew(alert) {
  if (!alert || !alert.kind) return false;
  if (!isNative()) return false;
  const todayKey = localDateStr();
  const last = (() => { try { return JSON.parse(localStorage.getItem(DAY_KEY) || "null"); } catch { return null; } })();
  // last shape: { date, kinds: [...] }
  if (last && last.date === todayKey && Array.isArray(last.kinds) && last.kinds.includes(alert.kind)) {
    return false; // already fired today for this kind
  }
  const ok = await ensurePermission();
  if (!ok) return false;
  const fireAt = new Date(Date.now() + 2000);
  try { await LocalNotifications.cancel({ notifications: [{ id: ANOMALY_NOT_ID }] }); } catch {}
  await LocalNotifications.schedule({
    notifications: [{
      id: ANOMALY_NOT_ID,
      title: alert.kind === 'illness'
        ? '⚠️ Possible illness — don\'t train hard'
        : '💧 Likely dehydrated — rehydrate first',
      body: alert.detail || alert.title || '',
      schedule: { at: fireAt, allowWhileIdle: true },
    }],
  });
  const next = last && last.date === todayKey
    ? { date: todayKey, kinds: Array.from(new Set([...(last.kinds || []), alert.kind])) }
    : { date: todayKey, kinds: [alert.kind] };
  try { localStorage.setItem(DAY_KEY, JSON.stringify(next)); } catch {}
  return true;
}

// Daily 06:30 nudge — generic so it works regardless of whether an
// anomaly is present. Tapping opens the app; the in-app Readiness card
// surfaces the specific anomaly if one exists. Idempotent: re-scheduling
// the same id replaces the existing trigger.
export async function scheduleMorningCheck(hh = DEFAULT_HH, mm = DEFAULT_MM) {
  if (!isNative()) return false;
  const ok = await ensurePermission();
  if (!ok) return false;
  try {
    try { await LocalNotifications.cancel({ notifications: [{ id: MORNING_NOT_ID }] }); } catch {}
    await LocalNotifications.schedule({
      notifications: [{
        id: MORNING_NOT_ID,
        title: '🩺 Morning check',
        body: "Tap to see today's Readiness verdict + recovery flags.",
        schedule: { on: { hour: hh, minute: mm }, allowWhileIdle: true },
      }],
    });
    return true;
  } catch { return false; }
}

// Cancel the daily 06:30 morning-check notification (Yusuf opted out).
export async function cancelMorningCheck() {
  if (!isNative()) return false;
  try { await LocalNotifications.cancel({ notifications: [{ id: MORNING_NOT_ID }] }); return true; }
  catch { return false; }
}

export async function initReadinessAlerts() {
  if (!isNative()) return;
  // Morning readiness nudge disabled per user request — cancel any previously
  // scheduled repeating notification so it stops firing.
  await cancelMorningCheck();
}
